import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ConversationView from './ConversationView';
import NewMessageModal from './NewMessageModal';
import './MessagesPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MessagesPage = ({ onUnreadCountChange, pendingConversationUser, onClearPendingUser }) => {
  const { authFetch } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [preselectedUser, setPreselectedUser] = useState(null);

  // Handle pending conversation user from search
  useEffect(() => {
    if (pendingConversationUser) {
      setPreselectedUser(pendingConversationUser);
      setShowNewMessage(true);
      if (onClearPendingUser) {
        onClearPendingUser();
      }
    }
  }, [pendingConversationUser, onClearPendingUser]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await authFetch('/api/messages/conversations');
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
        if (onUnreadCountChange) {
          onUnreadCountChange(data.unread_total || 0);
        }
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, onUnreadCountChange]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Polling for new messages
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadConversations();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [loadConversations]);

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    // Mark as read and update unread count
    loadConversations();
  };

  const handleBack = () => {
    setSelectedConversation(null);
    loadConversations();
  };

  const handleNewConversationCreated = (conversationId) => {
    setShowNewMessage(false);
    loadConversations();
    // Find and select the new conversation
    setTimeout(async () => {
      try {
        const res = await authFetch(`/api/messages/conversations/${conversationId}`);
        if (res.ok) {
          const data = await res.json();
          setSelectedConversation(data.conversation);
        }
      } catch (err) {
        console.error('Failed to load new conversation:', err);
      }
    }, 100);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-GB', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
  };

  if (loading) {
    return (
      <div className="messages-page">
        <div className="messages-container">
          <div className="messages-loading">Loading messages...</div>
        </div>
      </div>
    );
  }

  // Show conversation view if one is selected
  if (selectedConversation) {
    return (
      <ConversationView
        conversation={selectedConversation}
        onBack={handleBack}
        onMessageSent={loadConversations}
      />
    );
  }

  return (
    <div className="messages-page">
      <div className="messages-container">
        <div className="messages-header">
          <div className="messages-header__left">
            <h1>Messages</h1>
          </div>
          <button
            className="btn-new-message"
            onClick={() => setShowNewMessage(true)}
          >
            + New Message
          </button>
        </div>

        {conversations.length === 0 ? (
          <div className="messages-empty">
            <div className="messages-empty__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3>No messages yet</h3>
            <p>Start a conversation with someone you follow</p>
            <button
              className="btn-primary"
              onClick={() => setShowNewMessage(true)}
            >
              Send a Message
            </button>
          </div>
        ) : (
          <div className="conversations-list">
            {conversations.map((conversation) => {
              const otherUser = conversation.other_user || {};
              let profileImageUrl = otherUser.profile_image;
              if (profileImageUrl && !profileImageUrl.startsWith('http')) {
                profileImageUrl = `${API_BASE_URL}${profileImageUrl}`;
              }

              return (
                <div
                  key={conversation.id}
                  className={`conversation-item ${conversation.unread_count > 0 ? 'conversation-item--unread' : ''}`}
                  onClick={() => handleConversationSelect(conversation)}
                >
                  <div className={`conversation-item__avatar ${profileImageUrl ? 'has-image' : ''}`}>
                    {profileImageUrl ? (
                      <img src={profileImageUrl} alt={otherUser.name || 'User'} />
                    ) : (
                      (otherUser.name || otherUser.email || '?').charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="conversation-item__content">
                    <div className="conversation-item__header">
                      <span className="conversation-item__name">
                        {otherUser.name || otherUser.email || 'Unknown User'}
                      </span>
                      <span className="conversation-item__time">
                        {formatTime(conversation.last_message_at || conversation.created_at)}
                      </span>
                    </div>
                    <div className="conversation-item__preview">
                      {conversation.last_message ? (
                        <span className="conversation-item__message">
                          {conversation.last_message.content.length > 50
                            ? conversation.last_message.content.substring(0, 50) + '...'
                            : conversation.last_message.content}
                        </span>
                      ) : (
                        <span className="conversation-item__message conversation-item__message--empty">
                          No messages yet
                        </span>
                      )}
                      {conversation.unread_count > 0 && (
                        <span className="conversation-item__badge">
                          {conversation.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showNewMessage && (
        <NewMessageModal
          onClose={() => {
            setShowNewMessage(false);
            setPreselectedUser(null);
          }}
          onConversationCreated={handleNewConversationCreated}
          preselectedUser={preselectedUser}
        />
      )}
    </div>
  );
};

export default MessagesPage;
